import { accountIdFromPath, siblingAccountId } from "../util/accountId";
import authStore, {
    enterInspectFromEntry,
} from "./accessToken/observables/authStore";
import { authenticate } from "./auth";
import { getInspectToken } from "./inspectTokens";
import { getCognitoSession } from "./localStorage";

/**
 * Authenticate the base (logged-in) session, then resume inspect mode for this
 * tab if its `/:accountId` has a stored inspect token. Returns "app" to render
 * the app or "login" to show the login screen.
 */
export const bootstrapAuth = async (
    pathname: string,
): Promise<"app" | "login"> => {
    const baseSession = getCognitoSession();
    const authResult = await authenticate(baseSession);
    if (!authResult) {
        return "login";
    }

    authStore.dispatch("setAuthenticatedUser", { accountUser: authResult });

    const accountId = accountIdFromPath(pathname);
    if (accountId) {
        let entry = getInspectToken(accountId);
        if (!entry) {
            // The tab may have toggled env (P<->T) client-side after inspect
            // began; that navigation never wrote an entry for the new mode. If
            // the sibling mode has one, resume inspect in the current mode by
            // minting a fresh exchange token as the partner in this mode (the
            // empty token forces enterInspectFromEntry to re-mint).
            const sibling = siblingAccountId(accountId);
            const siblingEntry = sibling ? getInspectToken(sibling) : undefined;
            if (siblingEntry) {
                entry = {
                    token: "",
                    exp: 0,
                    partnerId: `${accountId[0]}${siblingEntry.partnerId.substring(1)}`,
                };
            }
        }
        if (entry) {
            try {
                await enterInspectFromEntry(accountId, entry);
            } catch (error) {
                // Inspect re-entry is best-effort: a failed exchange/account
                // call must not reject bootstrap, or the index.tsx catch clears
                // the base session and logs the admin out. Fall through to the
                // already-authenticated base session (normal mode) instead.
                console.error("Failed to resume inspect mode", error);
            }
        }
    }
    return "app";
};
